import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Save, Download, Mail, Check, FileText, ChevronLeft, ChevronRight } from "lucide-react";
import { useAppState } from "../hooks/useAppState";
import { useAuth } from "../hooks/useAuth";
import TopBar from "../components/layout/TopBar";
import TimesheetCalendar from "../components/timesheet/TimesheetCalendar";
import TimesheetSummary from "../components/timesheet/TimesheetSummary";
import { TIMESHEET_STATUS } from "../constants/timesheetStatus";
import { generateTimesheetPdf } from "../utils/timesheetPdf";
import { sendTimesheetEmail } from "../utils/timesheetEmail";

const MONTH_NAMES = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

export default function TimesheetPage() {
  const {
    user,
    darkMode,
    toggleDarkMode,
    isSynced,
    clients,
    timesheets,
    createTimesheet,
    updateTimesheet,
  } = useAppState();

  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Stato selezione
  const now = new Date();
  const [selectedClientId, setSelectedClientId] = useState(searchParams.get("clientId") || "");
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  // Stato locale entries
  const [localEntries, setLocalEntries] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Clienti filtrati: solo quelli con tipo daily/hourly/fixed
  const eligibleClients = useMemo(() => {
    return clients.filter(
      (c) => c.compensationType === "daily" || c.compensationType === "hourly" || c.compensationType === "fixed"
    );
  }, [clients]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === selectedClientId),
    [clients, selectedClientId]
  );

  // Cerca timesheet esistente
  const existingTimesheet = useMemo(() => {
    return timesheets.find(
      (t) =>
        t.clientId === selectedClientId &&
        t.month === selectedMonth &&
        t.year === selectedYear
    );
  }, [timesheets, selectedClientId, selectedMonth, selectedYear]);

  // Sync entries quando cambia il timesheet selezionato
  useEffect(() => {
    if (existingTimesheet) {
      setLocalEntries(existingTimesheet.entries || []);
      setDirty(false);
    } else {
      setLocalEntries([]);
      setDirty(false);
    }
  }, [existingTimesheet]);

  // Calcola totali
  const totalDays = useMemo(() => {
    const full = localEntries.filter((e) => e.value === 1).length;
    const half = localEntries.filter((e) => e.value === 0.5).length;
    return full + half * 0.5;
  }, [localEntries]);

  const rate = selectedClient?.rate || 0;
  const compensationType = selectedClient?.compensationType || "daily";
  const totalAmount = (compensationType === "daily" || compensationType === "hourly")
    ? totalDays * rate
    : compensationType === "fixed"
      ? rate
      : 0;

  const status = existingTimesheet?.status || TIMESHEET_STATUS.DRAFT;

  const handleEntryChange = useCallback((day, value, task) => {
    setLocalEntries((prev) => {
      const others = prev.filter((e) => e.day !== day);
      if (value === null && !task) return others;
      return [...others, { day, value, task }].sort((a, b) => a.day - b.day);
    });
    setDirty(true);
  }, []);

  const handleSave = async () => {
    if (!selectedClientId) return;
    setSaving(true);
    try {
      const data = {
        clientId: selectedClientId,
        clientName: selectedClient?.name || "",
        year: selectedYear,
        month: selectedMonth,
        status: existingTimesheet?.status || TIMESHEET_STATUS.DRAFT,
        entries: localEntries,
        totalDays,
        totalAmount,
        rate,
        compensationType,
        notes: existingTimesheet?.notes || "",
      };

      if (existingTimesheet) {
        await updateTimesheet(existingTimesheet.id, data);
      } else {
        await createTimesheet(data);
      }
      setDirty(false);
    } catch (err) {
      console.error("Error saving timesheet:", err);
      alert("Errore nel salvare il timesheet.");
    }
    setSaving(false);
  };

  const handleDownloadPdf = () => {
    const ts = existingTimesheet || {
      clientName: selectedClient?.name || "",
      year: selectedYear,
      month: selectedMonth,
      entries: localEntries,
      totalDays,
      totalAmount,
      rate,
      compensationType,
    };
    generateTimesheetPdf(ts, selectedClient, user);
  };

  const handleSendEmail = async () => {
    if (!existingTimesheet) {
      alert("Salva il timesheet prima di inviarlo.");
      return;
    }
    setSendingEmail(true);
    try {
      const { sentTo, sentAt } = await sendTimesheetEmail(existingTimesheet, selectedClient, user, authUser?.uid);
      await updateTimesheet(existingTimesheet.id, {
        status: TIMESHEET_STATUS.SENT,
        sentAt,
        sentTo,
      });
      alert(`Timesheet inviato a ${sentTo}`);
    } catch (err) {
      alert(err.message || "Errore nell'invio dell'email.");
    }
    setSendingEmail(false);
  };

  const handleMarkApproved = async () => {
    if (!existingTimesheet) return;
    await updateTimesheet(existingTimesheet.id, {
      status: TIMESHEET_STATUS.APPROVED,
    });
  };

  // Apre il form fattura precompilato per revisione (non crea la fattura direttamente).
  // La fattura viene effettivamente creata solo quando l'utente conferma il modale
  // in InvoicesPage (mode "duplicate" su initialValues = invoicePrefill).
  const handleCreateInvoice = () => {
    // Disponibile in DRAFT/SENT/APPROVED (serve un timesheet salvato); non in INVOICED.
    if (!existingTimesheet || status === TIMESHEET_STATUS.INVOICED) return;

    navigate("/invoices", {
      state: {
        invoicePrefill: {
          clientName: selectedClient?.name || existingTimesheet.clientName || "",
          grossAmount: totalAmount,
          notes: `Timesheet ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`,
          paid: false,
        },
        timesheetId: existingTimesheet.id,
      },
    });
  };

  const handleNavigateToInvoice = () => {
    navigate("/invoices");
  };

  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear((y) => y - 1);
    } else {
      setSelectedMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear((y) => y + 1);
    } else {
      setSelectedMonth((m) => m + 1);
    }
  };

  const isReadOnly = status === TIMESHEET_STATUS.INVOICED;

  return (
    <div className={`min-h-screen ${darkMode ? "bg-neutral-950 text-white" : "bg-neutral-50 text-neutral-900"}`}>
      <TopBar user={user} darkMode={darkMode} toggleDarkMode={toggleDarkMode} isSynced={isSynced} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <h1 className="text-xl font-bold mb-6">Timesheet</h1>

        {/* Selettori */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          {/* Cliente */}
          <select
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(e.target.value)}
            className={`flex-1 px-3 py-2 border rounded-lg text-sm ${
              darkMode
                ? "border-neutral-700 bg-neutral-800 text-white"
                : "border-neutral-200 bg-white text-neutral-900"
            }`}
          >
            <option value="">Seleziona cliente...</option>
            {eligibleClients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.compensationType === "daily" ? "giornaliero" : c.compensationType === "hourly" ? "orario" : "fisso"})
              </option>
            ))}
          </select>

          {/* Navigazione mese */}
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrevMonth}
              className={`p-2 rounded-lg border ${
                darkMode
                  ? "border-neutral-700 hover:bg-neutral-800 text-neutral-400"
                  : "border-neutral-200 hover:bg-neutral-100 text-neutral-600"
              }`}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className={`text-sm font-medium min-w-[140px] text-center ${darkMode ? "text-white" : "text-neutral-900"}`}>
              {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
            </span>
            <button
              onClick={handleNextMonth}
              className={`p-2 rounded-lg border ${
                darkMode
                  ? "border-neutral-700 hover:bg-neutral-800 text-neutral-400"
                  : "border-neutral-200 hover:bg-neutral-100 text-neutral-600"
              }`}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {!selectedClientId ? (
          <div className={`text-center py-16 rounded-xl border ${darkMode ? "border-neutral-800 bg-neutral-900" : "border-neutral-200 bg-white"}`}>
            <p className={`text-sm ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>
              {eligibleClients.length === 0
                ? "Nessun cliente con tariffa giornaliera, oraria o fissa. Crea un cliente prima."
                : "Seleziona un cliente per iniziare."}
            </p>
          </div>
        ) : (
          <>
            {/* Layout: Calendar + Summary */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
              {/* Calendar */}
              <div className={`rounded-xl border p-4 ${darkMode ? "border-neutral-800 bg-neutral-900" : "border-neutral-200 bg-white"}`}>
                <TimesheetCalendar
                  year={selectedYear}
                  month={selectedMonth}
                  entries={localEntries}
                  onEntryChange={handleEntryChange}
                  readOnly={isReadOnly}
                  darkMode={darkMode}
                />
              </div>

              {/* Summary */}
              <div className="space-y-4">
                <TimesheetSummary
                  year={selectedYear}
                  month={selectedMonth}
                  entries={localEntries}
                  rate={rate}
                  compensationType={compensationType}
                  status={status}
                  darkMode={darkMode}
                />

                {/* Azioni */}
                <div className={`rounded-xl border p-4 space-y-2 ${darkMode ? "border-neutral-800 bg-neutral-900" : "border-neutral-200 bg-white"}`}>
                  {/* Crea Fattura: disponibile in DRAFT/SENT/APPROVED (non in INVOICED). */}
                  {status !== TIMESHEET_STATUS.INVOICED && (
                    <button
                      onClick={handleCreateInvoice}
                      disabled={!existingTimesheet}
                      className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        !existingTimesheet
                          ? darkMode
                            ? "bg-neutral-800 text-neutral-500 cursor-not-allowed"
                            : "bg-neutral-100 text-neutral-400 cursor-not-allowed"
                          : "bg-forfy-500 text-white hover:bg-forfy-600"
                      }`}
                    >
                      <FileText className="h-4 w-4" />
                      Crea Fattura
                    </button>
                  )}

                  {/* Draft actions */}
                  {status === TIMESHEET_STATUS.DRAFT && (
                    <>
                      <button
                        onClick={handleSave}
                        disabled={saving || !dirty}
                        className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          dirty
                            ? "bg-forfy-500 text-white hover:bg-forfy-600"
                            : darkMode
                              ? "bg-neutral-800 text-neutral-500 cursor-not-allowed"
                              : "bg-neutral-100 text-neutral-400 cursor-not-allowed"
                        }`}
                      >
                        <Save className="h-4 w-4" />
                        {saving ? "Salvataggio..." : dirty ? "Salva bozza" : "Salvato"}
                      </button>
                      <button
                        onClick={handleDownloadPdf}
                        className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          darkMode
                            ? "border-neutral-700 text-neutral-300 hover:bg-neutral-800"
                            : "border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                        }`}
                      >
                        <Download className="h-4 w-4" />
                        Scarica PDF
                      </button>
                      <button
                        onClick={handleSendEmail}
                        disabled={sendingEmail || !existingTimesheet}
                        className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          !existingTimesheet
                            ? darkMode
                              ? "border-neutral-800 text-neutral-600 cursor-not-allowed"
                              : "border-neutral-100 text-neutral-300 cursor-not-allowed"
                            : darkMode
                              ? "border-neutral-700 text-neutral-300 hover:bg-neutral-800"
                              : "border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                        }`}
                      >
                        <Mail className="h-4 w-4" />
                        {sendingEmail ? "Invio..." : "Invia email"}
                      </button>
                    </>
                  )}

                  {/* Sent actions */}
                  {status === TIMESHEET_STATUS.SENT && (
                    <>
                      <button
                        onClick={handleMarkApproved}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors"
                      >
                        <Check className="h-4 w-4" />
                        Segna approvato
                      </button>
                      <button
                        onClick={handleDownloadPdf}
                        className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          darkMode
                            ? "border-neutral-700 text-neutral-300 hover:bg-neutral-800"
                            : "border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                        }`}
                      >
                        <Download className="h-4 w-4" />
                        Scarica PDF
                      </button>
                    </>
                  )}

                  {/* Approved actions */}
                  {status === TIMESHEET_STATUS.APPROVED && (
                    <>
                      <button
                        onClick={handleDownloadPdf}
                        className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          darkMode
                            ? "border-neutral-700 text-neutral-300 hover:bg-neutral-800"
                            : "border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                        }`}
                      >
                        <Download className="h-4 w-4" />
                        Scarica PDF
                      </button>
                    </>
                  )}

                  {/* Invoiced actions */}
                  {status === TIMESHEET_STATUS.INVOICED && (
                    <>
                      <button
                        onClick={handleDownloadPdf}
                        className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          darkMode
                            ? "border-neutral-700 text-neutral-300 hover:bg-neutral-800"
                            : "border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                        }`}
                      >
                        <Download className="h-4 w-4" />
                        Scarica PDF
                      </button>
                      <button
                        onClick={handleNavigateToInvoice}
                        className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          darkMode
                            ? "border-neutral-700 text-neutral-300 hover:bg-neutral-800"
                            : "border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                        }`}
                      >
                        <FileText className="h-4 w-4" />
                        Vai alla fattura
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
